// E8RightTriangleAreasPFClosedTask.js
// Zadanie zamkniete E8 P/F: pola trojkatow w trojkacie prostokatnym.

import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import TruthInput from "../../../../components/inputs/TruthInput";
import SvgMathLabel, { SvgRightAngle } from "../../../../components/diagrams/SvgMathLabel";

function pick(values) {
  return values[Math.floor(Math.random() * values.length)];
}

function buildData({ segment, height, hypotenuse, firstClaimArea, secondMultiplier }) {
  const smallArea = (segment * height) / 2;
  const wholeArea = (2 * segment * height) / 2;
  const adcArea = smallArea;

  return {
    segment,
    height,
    hypotenuse,
    smallArea,
    wholeArea,
    adcArea,
    firstClaimArea,
    secondMultiplier,
    correctPF: [
      firstClaimArea === smallArea ? "P" : "F",
      wholeArea === secondMultiplier * adcArea ? "P" : "F",
    ],
  };
}

function createOriginalTask() {
  return {
    type: "E8RightTriangleAreasPFClosedTask",
    data: buildData({
      segment: 30,
      height: 40,
      hypotenuse: 50,
      firstClaimArea: 600,
      secondMultiplier: 2,
    }),
    source: {
      variant: "original",
      exam: "E8",
      session: "2025_MAJ",
      taskNumber: 11,
    },
    tags: ["e8", "planimetria", "trojkaty", "pole_trojkata", "prawda_falsz"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  const scale = pick([4, 5, 6, 8, 10, 12]);
  const segment = 3 * scale;
  const height = 4 * scale;
  const hypotenuse = 5 * scale;
  const smallArea = (segment * height) / 2;
  const firstClaimArea = Math.random() < 0.55
    ? smallArea
    : pick([smallArea + segment, smallArea - segment, segment * height].filter((v) => v > 0));
  const secondMultiplier = pick([2, 2, 2, 3]);

  return {
    type: "E8RightTriangleAreasPFClosedTask",
    data: buildData({
      segment,
      height,
      hypotenuse,
      firstClaimArea,
      secondMultiplier,
    }),
    source: { variant: "random", basedOn: "E8_2025_MAJ_11" },
    tags: ["e8", "planimetria", "trojkaty", "pole_trojkata", "prawda_falsz"],
  };
}

function RightTriangleDiagram({ data }) {
  const line = "currentColor";
  const accent = "#f0ad3d";
  const fill = "rgba(240, 173, 61, 0.32)";

  // Wierzchołki
  const A = { x: 70, y: 200 };
  const D = { x: 210, y: 200 };
  const B = { x: 350, y: 200 };
  const C = { x: 350, y: 60 };

  // Środek przeciwprostokątnej DC (do obrotu etykiety wzdłuż odcinka)
  const dcMid = { x: (D.x + C.x) / 2, y: (D.y + C.y) / 2 };
  const dcAngle = (Math.atan2(C.y - D.y, C.x - D.x) * 180) / Math.PI;

  return (
    <div style={{ display: "flex", justifyContent: "center", margin: "18px 0" }}>
      <svg width="420" height="250" viewBox="0 0 420 250" role="img" aria-label="Trójkąt prostokątny ABC z punktem D">
        <polygon points={`${A.x},${A.y} ${B.x},${B.y} ${C.x},${C.y}`} fill="none" stroke={line} strokeWidth="3" />
        <polygon points={`${D.x},${D.y} ${B.x},${B.y} ${C.x},${C.y}`} fill={fill} stroke={accent} strokeWidth="3" />
        <line x1={D.x} y1={D.y} x2={C.x} y2={C.y} stroke={accent} strokeWidth="3" />

        {/* kąt prosty przy B, kropka w środku znacznika (wnętrze) */}
        <SvgRightAngle V={B} u={{ x: -1, y: 0 }} v={{ x: 0, y: -1 }} radius={22} color={line} />

        {/* litery wierzchołków (KaTeX) */}
        <SvgMathLabel x={A.x - 14} y={A.y + 14} math="A" />
        <SvgMathLabel x={D.x} y={D.y + 16} math="D" />
        <SvgMathLabel x={B.x + 14} y={B.y + 14} math="B" />
        <SvgMathLabel x={C.x + 14} y={C.y - 8} math="C" />

        {/* wymiary (KaTeX) */}
        <SvgMathLabel x={(A.x + D.x) / 2} y={A.y + 20} math={`${data.segment}\\text{ cm}`} fontSize={15} />
        <SvgMathLabel x={(D.x + B.x) / 2} y={B.y + 20} math={`${data.segment}\\text{ cm}`} fontSize={15} />
        <g transform={`rotate(${dcAngle.toFixed(1)} ${dcMid.x} ${dcMid.y})`}>
          <SvgMathLabel x={dcMid.x} y={dcMid.y - 14} math={`${data.hypotenuse}\\text{ cm}`} fontSize={15} color={accent} />
        </g>
      </svg>
    </div>
  );
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Dany jest trójkąt prostokątny <InlineMath math="ABC" />. Na środku boku{" "}
        <InlineMath math="AB" /> zaznaczono punkt <InlineMath math="D" />.
        Następnie poprowadzono odcinek <InlineMath math="DC" />, dzielący trójkąt{" "}
        <InlineMath math="ABC" /> na dwa trójkąty <InlineMath math="ADC" /> i{" "}
        <InlineMath math="DBC" />. Ponadto{" "}
        <InlineMath math={`|AD|=|DB|=${data.segment}\\ \\text{cm}`} /> oraz{" "}
        <InlineMath math={`|DC|=${data.hypotenuse}\\ \\text{cm}`} />.
      </p>
      <RightTriangleDiagram data={data} />
      <p>
        Oceń prawdziwość podanych zdań. Wybierz <InlineMath math="P" />, jeśli
        zdanie jest prawdziwe, albo <InlineMath math="F" />, jeśli jest fałszywe.
      </p>
      <p>
        1. Pole trójkąta <InlineMath math="DBC" /> jest równe{" "}
        <InlineMath math={`${data.firstClaimArea}\\ \\text{cm}^2`} />.
      </p>
      <p>
        2. Pole trójkąta <InlineMath math="ABC" /> jest{" "}
        <InlineMath math={`${data.secondMultiplier}`} /> razy większe od pola
        trójkąta <InlineMath math="ADC" />.
      </p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const value = userAnswer || {};
  const setValue = (key, nextValue) =>
    onChange?.({ ...value, [key]: nextValue });

  return (
    <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
      {[["s1", "1."], ["s2", "2."]].map(([key, label]) => (
        <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ minWidth: 18 }}>{label}</span>
          <TruthInput
            value={value[key] || null}
            disabled={disabled}
            onChange={(nextValue) => setValue(key, nextValue)}
          />
        </div>
      ))}
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  return (
    userAnswer?.s1 === data.correctPF[0] &&
    userAnswer?.s2 === data.correctPF[1]
  );
}

export function renderAnswer({ data }) {
  return (
    <InlineMath
      math={`1.\\ \\text{${data.correctPF[0]}},\\qquad 2.\\ \\text{${data.correctPF[1]}}`}
    />
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer?.s1 && !userAnswer?.s2) return <i>(brak odpowiedzi)</i>;
  return (
    <InlineMath
      math={`1.\\ \\text{${userAnswer?.s1 || "-"}},\\qquad 2.\\ \\text{${userAnswer?.s2 || "-"}}`}
    />
  );
}

export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> W trójkącie <InlineMath math="DBC" /> znamy
        przeciwprostokątną <InlineMath math="DC" /> i jedną przyprostokątną{" "}
        <InlineMath math="DB" />. Obliczamy drugą przyprostokątną{" "}
        <InlineMath math="BC" /> z twierdzenia Pitagorasa.
      </p>
      <BlockMath
        math={`BC^2=${data.hypotenuse}^2-${data.segment}^2=${data.hypotenuse ** 2}-${data.segment ** 2}=${data.height ** 2}`}
      />
      <BlockMath math={`BC=${data.height}\\ \\text{cm}`} />
      <p>
        <strong>Krok 2.</strong> Obliczamy pole trójkąta{" "}
        <InlineMath math="DBC" />. Przyprostokątne są prostopadłe, więc mogą być
        podstawą i wysokością.
      </p>
      <BlockMath
        math={`P_{DBC}=\\frac{${data.segment}\\cdot${data.height}}{2}=${data.smallArea}\\ \\text{cm}^2`}
      />
      <p>
        Pierwsze zdanie jest{" "}
        <strong>{data.correctPF[0] === "P" ? "prawdziwe" : "fałszywe"}</strong>.
      </p>
      <p>
        <strong>Krok 3.</strong> Punkt <InlineMath math="D" /> jest środkiem
        odcinka <InlineMath math="AB" />, więc{" "}
        <InlineMath math={`AB=${data.segment}+${data.segment}=${2 * data.segment}`} />.
        Wysokość całego trójkąta <InlineMath math="ABC" /> jest taka sama jak
        wysokość trójkątów <InlineMath math="ADC" /> i <InlineMath math="DBC" />.
      </p>
      <BlockMath
        math={`P_{ABC}=\\frac{${2 * data.segment}\\cdot${data.height}}{2}=${data.wholeArea}\\ \\text{cm}^2`}
      />
      <BlockMath
        math={`P_{ADC}=\\frac{${data.segment}\\cdot${data.height}}{2}=${data.adcArea}\\ \\text{cm}^2`}
      />
      <p>
        <strong>Krok 4.</strong> Porównujemy pola.
      </p>
      <BlockMath
        math={`${data.wholeArea}=${data.wholeArea / data.adcArea}\\cdot${data.adcArea}`}
      />
      <p>
        Drugie zdanie jest{" "}
        <strong>{data.correctPF[1] === "P" ? "prawdziwe" : "fałszywe"}</strong>.
      </p>
      <p>
        <strong>Krok 5.</strong> Zaznaczamy odpowiedzi{" "}
        <InlineMath
          math={`1.\\ \\text{${data.correctPF[0]}}\\quad\\text{i}\\quad 2.\\ \\text{${data.correctPF[1]}}`}
        />
        .
      </p>
    </div>
  );
}
